// External libraries
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";

// Project components
import Button from "../../../../Components/Button";
import Multiselect from "../../../../Components/Inputs/Multiselect";
import TextInput from "../../../../Components/Inputs/Text";
import Spacer from "../../../../Components/Spacer";
import Tabs from "../../../../Components/Tabs";
import { Text } from "../../../../Components/Text";

// Feature/context-specific hooks and constants
import { useConfiguration } from "../../contexts/ConfigurationContext";
import { useCustomizedData } from "../../contexts/CustomizedDataContext";

// Styles
import css from "../styles.styl";

// Local/relative feature components
import { ReviewFormsTable, ReviewTemplatesTable, ReviewCyclesTable } from "./tables";

const REVIEW_QUESTION_TYPES = ["text", "multiple-choice", "date", "multiline", "numeric"];

const AUTHOR_OR_SIGNER_OPTIONS = [
	{ label: "Manager", value: "manager" },
	{ label: "Self", value: "self" },
];

const BOOLEAN_OPTIONS = [
	{ label: "True", value: "true" },
	{ label: "False", value: "false" },
];

const WHO_CAN_ADD_FORM_OPTIONS = [
	{ label: "Any", value: "any" },
	{ label: "Manager", value: "manager" },
	{ label: "Subject", value: "subject" },
	{ label: "Admin", value: "admin" },
];

const APPROVAL_REQUIRED_OPTIONS = [
	{ label: "None", value: "no" },
	{ label: "Manager and Admins", value: "managers-and-admins" },
	{ label: "Admins", value: "admins" },
];

const ReviewData = () => {
	const { showError, hideError, error } = useConfiguration();
	const {
		customReviewFormData,
		updateCustomReviewFormData,
		addQuestion,
		updateQuestion,
		removeQuestion,
		addCustomReviewForm: addCustomReviewFormToContext,
		editCustomReviewForm: editCustomReviewFormInContext,
		deleteCustomReviewForm: deleteCustomReviewFormFromContext,
		cancelEditCustomReviewForm,
		reviewTemplateData,
		updateReviewTemplateData,
		removeReviewTemplateForm,
		addReviewTemplate,
		editReviewTemplate,
		deleteReviewTemplate,
		cancelEditReviewTemplate,
		reviewCycleData,
		updateReviewCycleData,
		addReviewCycle: addReviewCycleToContext,
		editReviewCycle,
		deleteReviewCycle,
		cancelEditReviewCycle,
		employeeData,
		employeeGroupData,
	} = useCustomizedData();

	const [currentTab, setCurrentTab] = useState("Review Forms");

	const handleTabChange = (tab) => {
		setCurrentTab(tab);
	};

	const addCustomReviewForm = () => {
		if (!customReviewFormData.customReviewFormName) {
			showError("Please enter a review form name.");
			return;
		}

		if (customReviewFormData.customReviewQuestions.length === 0) {
			showError("Please add at least one question to the review form.");
			return;
		}

		for (const question of customReviewFormData.customReviewQuestions) {
			if (!question.question) {
				showError("Please enter text for all questions.");
				return;
			}
			if (!question.type) {
				showError("Please select a type for all questions.");
				return;
			}
			if (question.type === "multiple-choice" && !question.choices) {
				showError("Please enter choices for all multiple-choice questions.");
				return;
			}
		}

		const filteredQuestions = customReviewFormData.customReviewQuestions.filter(
			(question) => question.question && question.type
		);
		if (filteredQuestions.some((question) => question.type === "multiple-choice")) {
			for (const question of filteredQuestions) {
				if (question.type === "multiple-choice" && question.choices && typeof question.choices === "string") {
					question.choices = question.choices.split(",");
				}
			}
		}

		const formToSave = {
			name: customReviewFormData.customReviewFormName,
			questions: filteredQuestions,
		};

		if (!customReviewFormData.editingCustomReviewFormId) {
			formToSave.id = Date.now();
		}

		addCustomReviewFormToContext(formToSave);
		hideError();
	};

	const getFormNameById = (form) => {
		if (typeof form === "string") {
			return form;
		}
		return form.form || `Form ${form.id}`;
	};

	const addReviewTemplateToContext = () => {
		if (!reviewTemplateData.reviewTemplateName) {
			showError("Please enter a template name.");
			return;
		}

		const forms = (reviewTemplateData.forms || []).filter((form) => form.form);
		const forms360 = (reviewTemplateData.forms_360 || []).filter((form) => form.form);
		const externalForms = (reviewTemplateData.external_forms || []).filter((form) => form.form);

		if (forms.length === 0 && forms360.length === 0 && externalForms.length === 0) {
			showError("Please add at least one form to the template.");
			return;
		}

		const templateToSave = {
			name: reviewTemplateData.reviewTemplateName,
			forms,
			forms_360: forms360,
			external_forms: externalForms,
		};

		if (!reviewTemplateData.editingReviewTemplateId) {
			templateToSave.id = Date.now();
		}

		addReviewTemplate(templateToSave);
		hideError();
	};

	const renderDynamicFormFields = () => {
		const formType = reviewTemplateData.selectedFormType?.value;
		if (!formType) return null;

		return (
			<div>
				{formType === "regular" && (
					<>
						<Text variant="body">Author</Text>
						<Select
							options={AUTHOR_OR_SIGNER_OPTIONS}
							value={reviewTemplateData.tempFormData?.author}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, author: val },
								});
								hideError();
							}}
							placeholder="Select author..."
						/>
						<Spacer size={1} />

						<Text variant="body">Signer</Text>
						<Select
							options={AUTHOR_OR_SIGNER_OPTIONS}
							value={reviewTemplateData.tempFormData?.signer}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, signer: val },
								});
								hideError();
							}}
							placeholder="Select signer..."
						/>
						<Spacer size={1} />

						<Text variant="body">Visibility</Text>
						<Select
							options={BOOLEAN_OPTIONS}
							value={reviewTemplateData.tempFormData?.visibility}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, visibility: val },
								});
								hideError();
							}}
							placeholder="Select visibility..."
						/>
						<Spacer size={1} />

						<Text variant="body">Redact</Text>
						<Select
							options={BOOLEAN_OPTIONS}
							value={reviewTemplateData.tempFormData?.redact}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, redact: val },
								});
								hideError();
							}}
							placeholder="Select redact option..."
						/>
						<Spacer size={1} />

						<Text variant="body">Author Days to Complete</Text>
						<TextInput
							value={reviewTemplateData.tempFormData?.authorDaysToComplete || ""}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, authorDaysToComplete: val },
								});
								hideError();
							}}
							placeholder="Enter days..."
						/>
						<Spacer size={1} />

						<Text variant="body">Signer Days to Complete</Text>
						<TextInput
							value={reviewTemplateData.tempFormData?.signerDaysToComplete || ""}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, signerDaysToComplete: val },
								});
								hideError();
							}}
							placeholder="Enter days..."
						/>
					</>
				)}

				{formType === "360" && (
					<>
						<Text variant="body">Who Can Add Form</Text>
						<Select
							options={WHO_CAN_ADD_FORM_OPTIONS}
							value={reviewTemplateData.tempFormData?.whoCanAddForm}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, whoCanAddForm: val },
								});
								hideError();
							}}
							placeholder="Select who can add form..."
						/>
						<Spacer size={1} />

						<Text variant="body">Signer</Text>
						<Select
							options={AUTHOR_OR_SIGNER_OPTIONS}
							value={reviewTemplateData.tempFormData?.signer}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, signer: val },
								});
								hideError();
							}}
							placeholder="Select signer..."
						/>
						<Spacer size={1} />

						<Text variant="body">Author Days to Complete</Text>
						<TextInput
							value={reviewTemplateData.tempFormData?.authorDaysToComplete || ""}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, authorDaysToComplete: val },
								});
								hideError();
							}}
							placeholder="Enter days..."
						/>
						<Spacer size={1} />

						<Text variant="body">Signer Days to Complete</Text>
						<TextInput
							value={reviewTemplateData.tempFormData?.signerDaysToComplete || ""}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, signerDaysToComplete: val },
								});
								hideError();
							}}
							placeholder="Enter days..."
						/>
						<Spacer size={1} />

						<Text variant="body">Approval Required</Text>
						<Select
							options={APPROVAL_REQUIRED_OPTIONS}
							value={reviewTemplateData.tempFormData?.approvalRequired}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, approvalRequired: val },
								});
								hideError();
							}}
							placeholder="Select approval requirement..."
						/>
						<Spacer size={1} />

						<Text variant="body">Visibility</Text>
						<Select
							options={BOOLEAN_OPTIONS}
							value={reviewTemplateData.tempFormData?.visibility}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, visibility: val },
								});
								hideError();
							}}
							placeholder="Select visibility..."
						/>
						<Spacer size={1} />

						<Text variant="body">Allow Request Message</Text>
						<Select
							options={BOOLEAN_OPTIONS}
							value={reviewTemplateData.tempFormData?.allowRequestMessage}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, allowRequestMessage: val },
								});
								hideError();
							}}
							placeholder="Select allow request message..."
						/>
						<Spacer size={1} />

						<Text variant="body">Redact</Text>
						<Select
							options={BOOLEAN_OPTIONS}
							value={reviewTemplateData.tempFormData?.redact}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, redact: val },
								});
								hideError();
							}}
							placeholder="Select redact option..."
						/>
					</>
				)}

				{formType === "external" && (
					<>
						<Text variant="body">Reviewer Name</Text>
						<TextInput
							value={reviewTemplateData.tempFormData?.reviewerName || ""}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, reviewerName: val },
								});
								hideError();
							}}
							placeholder="Enter reviewer name..."
						/>
						<Spacer size={1} />

						<Text variant="body">Reviewer Email</Text>
						<TextInput
							value={reviewTemplateData.tempFormData?.reviewerEmail || ""}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, reviewerEmail: val },
								});
								hideError();
							}}
							placeholder="Enter reviewer email..."
						/>
						<Spacer size={1} />

						<Text variant="body">Message</Text>
						<TextInput
							value={reviewTemplateData.tempFormData?.message || ""}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, message: val },
								});
								hideError();
							}}
							placeholder="Enter message..."
						/>
						<Spacer size={1} />

						<Text variant="body">Visibility</Text>
						<Select
							options={BOOLEAN_OPTIONS}
							value={reviewTemplateData.tempFormData?.visibility}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, visibility: val },
								});
								hideError();
							}}
							placeholder="Select visibility..."
						/>
						<Spacer size={1} />

						<Text variant="body">Redact</Text>
						<Select
							options={BOOLEAN_OPTIONS}
							value={reviewTemplateData.tempFormData?.redact}
							onChange={(val) => {
								updateReviewTemplateData({
									tempFormData: { ...reviewTemplateData.tempFormData, redact: val },
								});
								hideError();
							}}
							placeholder="Select redact option..."
						/>
					</>
				)}
			</div>
		);
	};

	const addFormToTemplate = () => {
		const formType = reviewTemplateData.selectedFormType?.value;
		const formId = reviewTemplateData.selectedForm?.value;
		const formName = reviewTemplateData.selectedForm?.label;

		if (!formType || !formId) return;

		const { tempFormData } = reviewTemplateData;

		if (formType === "regular") {
			if (
				!tempFormData?.author ||
				!tempFormData?.signer ||
				!tempFormData?.visibility ||
				!tempFormData?.redact ||
				!tempFormData?.authorDaysToComplete ||
				!tempFormData?.signerDaysToComplete
			) {
				showError("Please fill out all fields for the regular form.");
				return;
			}
		} else if (formType === "360") {
			if (
				!tempFormData?.whoCanAddForm ||
				!tempFormData?.signer ||
				!tempFormData?.authorDaysToComplete ||
				!tempFormData?.signerDaysToComplete ||
				!tempFormData?.approvalRequired ||
				!tempFormData?.visibility ||
				!tempFormData?.allowRequestMessage ||
				!tempFormData?.redact
			) {
				showError("Please fill out all fields for the 360 form.");
				return;
			}
		} else if (formType === "external") {
			if (
				!tempFormData?.reviewerName ||
				!tempFormData?.reviewerEmail ||
				!tempFormData?.visibility ||
				!tempFormData?.redact
			) {
				showError(
					"Please fill out Reviewer Name, Reviewer Email, Visibility, and Redact for the external form."
				);
				return;
			}
		}

		const newFormId = Date.now();
		const baseForm = {
			id: newFormId,
			form: formName,
		};

		let newForm = { ...baseForm };

		if (formType === "regular") {
			newForm = {
				...newForm,
				authors: [tempFormData?.author?.value || "self"],
				signers: [tempFormData?.signer?.value || "manager"],
				visibility: tempFormData?.visibility?.value === "true",
				redact: tempFormData?.redact?.value === "true",
				authors_days_to_complete: parseInt(tempFormData?.authorDaysToComplete || "0", 10),
				signers_days_to_complete: parseInt(tempFormData?.signerDaysToComplete || "0", 10),
			};
		} else if (formType === "360") {
			newForm = {
				...newForm,
				who_can_add_form: tempFormData?.whoCanAddForm?.value || "any",
				signers: [tempFormData?.signer?.value || "manager"],
				authors_days_to_complete: parseInt(tempFormData?.authorDaysToComplete || "0", 10),
				signers_days_to_complete: parseInt(tempFormData?.signerDaysToComplete || "0", 10),
				approval_required: tempFormData?.approvalRequired?.value || "no",
				visibility: tempFormData?.visibility?.value === "true",
				allow_request_message: tempFormData?.allowRequestMessage?.value === "true",
				redact: tempFormData?.redact?.value === "true",
			};
		} else if (formType === "external") {
			newForm = {
				...newForm,
				reviewer_name: tempFormData?.reviewerName || "",
				reviewer_email: tempFormData?.reviewerEmail || "",
				message: tempFormData?.message || "",
				visibility: tempFormData?.visibility?.value === "true",
				redact: tempFormData?.redact?.value === "true",
			};
		}

		const formArrayKey = formType === "regular" ? "forms" : formType === "360" ? "forms_360" : "external_forms";

		updateReviewTemplateData({
			[formArrayKey]: [...(reviewTemplateData[formArrayKey] || []), newForm],
			selectedFormType: null,
			selectedForm: null,
			tempFormData: {},
		});
		hideError();
	};

	const addReviewCycle = () => {
		if (!reviewCycleData.reviewCycleName) {
			showError("Please enter a Review Cycle Name.");
			return;
		}
		if (!reviewCycleData.reviewTemplate) {
			showError("Please select a Review Template.");
			return;
		}
		if (!reviewCycleData.subjects || reviewCycleData.subjects.length === 0) {
			showError("Please select at least one subject.");
			return;
		}

		const cycleToSave = {
			name: reviewCycleData.reviewCycleName,
			reviewTemplate: reviewCycleData.reviewTemplate,
			admin_only: reviewCycleData.admin_only ? reviewCycleData.admin_only.value === "true" : false,
			group: reviewCycleData.group,
			startDate: reviewCycleData.startDate,
			subjects: reviewCycleData.subjects,
		};

		if (!reviewCycleData.editingReviewCycleId) {
			cycleToSave.id = Date.now();
		}

		addReviewCycleToContext(cycleToSave);
		hideError();
	};

	return (
		<div>
			<Tabs
				currentTab={currentTab}
				tabs={["Review Forms", "Review Templates", "Review Cycles"]}
				onChange={handleTabChange}
			/>
			<Spacer size={2} />

			{currentTab === "Review Forms" && (
				<div>
					{/* Review Form Section */}
					<div>
						<Text variant="h2" weight="bold">
							{customReviewFormData.editingCustomReviewFormId ? "Edit Review Form" : "Add Review Form"}
						</Text>
						<Spacer size={1} />
						<Text variant="body" weight="bold">
							Review Form Name
						</Text>
						<TextInput
							value={customReviewFormData.customReviewFormName}
							onChange={(val) => {
								updateCustomReviewFormData({ customReviewFormName: val });
								hideError();
							}}
						/>
						<Spacer size={2} />

						{customReviewFormData.customReviewQuestions.map((question, index) => (
							<div
								key={question.id}
								style={{
									border: "1px solid #e0e0e0",
									padding: "16px",
									marginBottom: "16px",
									borderRadius: "4px",
								}}
							>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										marginBottom: "8px",
									}}
								>
									<Text variant="body" weight="bold">
										Question {index + 1}
									</Text>
								</div>

								<Text variant="body" weight="bold">
									Question Text
								</Text>
								<TextInput
									value={question.question}
									onChange={(val) => {
										updateQuestion(question.id, { question: val });
										hideError();
									}}
									placeholder="Enter your question here..."
								/>
								<Spacer size={1} />

								<Text variant="body" weight="bold">
									Question Type
								</Text>
								<Select
									options={REVIEW_QUESTION_TYPES.map((type) => ({
										label: type,
										value: type,
									}))}
									value={question.type ? { label: question.type, value: question.type } : null}
									onChange={(val) => {
										updateQuestion(question.id, { type: val ? val.value : "" });
										hideError();
									}}
									placeholder="Select question type..."
								/>
								<Spacer size={1} />
								{question.type === "multiple-choice" && (
									<>
										<Text variant="body" weight="bold">
											Question Choices
										</Text>
										<Spacer size={1} />
										<Text variant="caption">
											Enter the choices for the multiple-choice question, separated by commas.
										</Text>
										<TextInput
											value={question.choices}
											onChange={(val) => {
												updateQuestion(question.id, { choices: val });
												hideError();
											}}
										/>
									</>
								)}
								<Spacer size={1} />
								<Text variant="body" weight="bold">
									Required
								</Text>
								<Select
									options={BOOLEAN_OPTIONS}
									value={question.required}
									onChange={(val) => updateQuestion(question.id, { required: val })}
								/>

								{customReviewFormData.customReviewQuestions.length > 1 && (
									<>
										<Spacer size={1} />
										<Button color="red" size="small" onClick={() => removeQuestion(question.id)}>
											Remove Question
										</Button>
									</>
								)}
							</div>
						))}

						<Button size="small" onClick={addQuestion} style={{ marginBottom: "16px" }}>
							Add Another Question
						</Button>
						<Spacer size={1} />

						<div style={{ display: "flex", gap: "10px" }}>
							<Button size="small" onClick={() => addCustomReviewForm()}>
								{customReviewFormData.editingCustomReviewFormId
									? "Update Review Form"
									: "Add Review Form"}
							</Button>
							{customReviewFormData.editingCustomReviewFormId && (
								<Button
									size="small"
									color="gray"
									onClick={() => {
										cancelEditCustomReviewForm();
										hideError();
									}}
								>
									Cancel
								</Button>
							)}
						</div>
						{error &&
							(error.includes("review form name") ||
								error.includes("question") ||
								error.includes("type") ||
								error.includes("choices")) && (
								<>
									<Spacer size={2} />
									<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
										<Text variant="h3" weight="bold" style={{ color: "red" }}>
											{error}
										</Text>
									</div>
									<Spacer size={1} />
								</>
							)}
						<Spacer size={2} />
						{customReviewFormData.customReviewForms.length > 0 && (
							<ReviewFormsTable
								reviewForms={customReviewFormData.customReviewForms}
								onEdit={editCustomReviewFormInContext}
								onDelete={deleteCustomReviewFormFromContext}
							/>
						)}
					</div>
				</div>
			)}

			{currentTab === "Review Templates" && (
				<div>
					{/* Review Template Section */}
					<div>
						<Text variant="h2" weight="bold">
							{reviewTemplateData.editingReviewTemplateId
								? "Edit Review Template"
								: "Add Review Template"}
						</Text>
						<Spacer size={1} />
						<Text variant="caption">Review forms must be added before creating a review template.</Text>
						<Spacer size={1} />
						<Text variant="body" weight="bold">
							Template Name
						</Text>
						<TextInput
							value={reviewTemplateData.reviewTemplateName}
							onChange={(val) => {
								updateReviewTemplateData({ reviewTemplateName: val });
								hideError();
							}}
						/>
						<Spacer size={2} />

						{/* Template Forms Section */}
						<Text variant="h3" weight="bold">
							Template Forms
						</Text>
						<Spacer size={1} />

						{/* Render all added forms */}
						{[
							...(reviewTemplateData.forms || []).map((form) => ({
								...form,
								type: "regular",
							})),
							...(reviewTemplateData.forms_360 || []).map((form) => ({
								...form,
								type: "360",
							})),
							...(reviewTemplateData.external_forms || []).map((form) => ({
								...form,
								type: "external",
							})),
						]
							.filter((form) => form.form)
							.map((form, index) => (
								<div
									key={`${form.type}-${form.id}`}
									style={{
										border: "1px solid #e0e0e0",
										padding: "16px",
										marginBottom: "16px",
										borderRadius: "4px",
									}}
								>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											marginBottom: "8px",
										}}
									>
										<Text variant="body" weight="bold">
											{form.type.charAt(0).toUpperCase() + form.type.slice(1)} Form:{" "}
											{getFormNameById(form)}
										</Text>
										<Spacer size={1} />
										<Button
											color="red"
											size="small"
											onClick={() => removeReviewTemplateForm(form.type, form.id)}
										>
											Remove Form
										</Button>
									</div>
									<Text variant="caption">
										Type: {form.type.charAt(0).toUpperCase() + form.type.slice(1)}
									</Text>
								</div>
							))}

						{/* Add New Form Section */}
						<div
							style={{
								border: "2px dashed #ccc",
								padding: "16px",
								marginBottom: "16px",
								borderRadius: "4px",
							}}
						>
							<Text variant="body" weight="bold">
								Add New Form
							</Text>
							<Spacer size={1} />

							<Text variant="body" weight="bold">
								Form Type
							</Text>
							<Select
								options={[
									{ label: "Regular", value: "regular" },
									{ label: "360", value: "360" },
									{ label: "External", value: "external" },
								]}
								value={reviewTemplateData.selectedFormType}
								onChange={(val) => updateReviewTemplateData({ selectedFormType: val })}
								placeholder="Select form type..."
							/>
							<Spacer size={1} />

							{reviewTemplateData.selectedFormType && (
								<>
									<Text variant="body" weight="bold">
										Form
									</Text>
									<Select
										options={customReviewFormData.customReviewForms.map((f) => ({
											label: f.name,
											value: f.id,
										}))}
										value={reviewTemplateData.selectedForm}
										onChange={(val) => updateReviewTemplateData({ selectedForm: val })}
										placeholder="Select a form..."
									/>
									<Spacer size={1} />

									{reviewTemplateData.selectedForm && renderDynamicFormFields()}

									<Spacer size={1} />
									<Button
										size="small"
										onClick={() => addFormToTemplate()}
										disabled={!reviewTemplateData.selectedForm}
									>
										Add Form to Template
									</Button>
								</>
							)}
						</div>

						<Spacer size={2} />
						<div style={{ display: "flex", gap: "10px" }}>
							<Button size="small" onClick={() => addReviewTemplateToContext()}>
								{reviewTemplateData.editingReviewTemplateId
									? "Update Review Template"
									: "Add Review Template"}
							</Button>
							{reviewTemplateData.editingReviewTemplateId && (
								<Button
									size="small"
									color="gray"
									onClick={() => {
										cancelEditReviewTemplate();
										hideError();
									}}
								>
									Cancel
								</Button>
							)}
						</div>

						{error &&
							(error.includes("template") ||
								error.includes("regular form") ||
								error.includes("360 form") ||
								error.includes("external form")) && (
								<>
									<Spacer size={2} />
									<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
										<Text variant="h3" weight="bold" style={{ color: "red" }}>
											{error}
										</Text>
									</div>
									<Spacer size={1} />
								</>
							)}
						<Spacer size={2} />
						{reviewTemplateData.reviewTemplates.length > 0 && (
							<ReviewTemplatesTable
								reviewTemplates={reviewTemplateData.reviewTemplates}
								onEdit={editReviewTemplate}
								onDelete={deleteReviewTemplate}
							/>
						)}
					</div>
				</div>
			)}

			{currentTab === "Review Cycles" && (
				<div>
					{/* Review Cycle Section */}
					<div>
						<Text variant="h2" weight="bold">
							{reviewCycleData.editingReviewCycleId ? "Edit Review Cycle" : "Add Review Cycle"}
						</Text>
						<Spacer size={1} />
						<Text variant="body" weight="bold">
							Review Cycle Name
						</Text>
						<TextInput
							value={reviewCycleData.reviewCycleName}
							onChange={(val) => {
								updateReviewCycleData({ reviewCycleName: val });
								hideError();
							}}
						/>
						<Spacer size={1} />
						<Text variant="body" weight="bold">
							Review Template
						</Text>
						<Select
							options={reviewTemplateData.reviewTemplates.map((t) => ({
								label: t.name,
								value: t.id,
							}))}
							value={reviewCycleData.reviewTemplate}
							onChange={(val) => {
								updateReviewCycleData({ reviewTemplate: val });
								hideError();
							}}
						/>
						<Spacer size={1} />
						<Text variant="body" weight="bold">
							Admin Only
						</Text>
						<Select
							options={BOOLEAN_OPTIONS}
							value={reviewCycleData.admin_only}
							onChange={(val) => {
								updateReviewCycleData({ admin_only: val });
								hideError();
							}}
						/>
						<Spacer size={1} />
						<Text variant="body" weight="bold">
							Group specific Review Cycle
						</Text>
						<Spacer size={1} />
						{employeeGroupData.employeeGroups.length === 0 ? (
							<>
								<Spacer size={1} />
								<Text variant="caption" weight="bold" style={{ color: "#666", fontStyle: "italic" }}>
									No employee groups found. Please add a group first.
								</Text>
								<Spacer size={1} />
							</>
						) : (
							<>
								<Select
									options={employeeGroupData.employeeGroups.map((g) => ({
										label: g.name,
										value: g.id,
									}))}
									value={reviewCycleData.group}
									onChange={(val) => updateReviewCycleData({ group: val })}
								/>
							</>
						)}
						<Spacer size={1} />

						<Text variant="body" weight="bold">
							Start Date
						</Text>
						<div className={css.inputContainer}>
							<DatePicker
								selected={reviewCycleData.startDate}
								onChange={(val) => updateReviewCycleData({ startDate: val })}
							/>
						</div>
						<Spacer size={1} />
						<Text variant="body" weight="bold">
							Subjects
						</Text>
						{employeeData.employees.length === 0 ? (
							<>
								<Spacer size={1} />
								<Text variant="caption" weight="bold" style={{ color: "#666", fontStyle: "italic" }}>
									No employees found. Please add employees first.
								</Text>
								<Spacer size={1} />
							</>
						) : (
							<Multiselect
								options={employeeData.employees.map((s) => ({ label: s.name, id: s.id }))}
								value={reviewCycleData.subjects}
								onChange={(val) => {
									updateReviewCycleData({ subjects: val });
									hideError();
								}}
								columns={4}
							/>
						)}
						<Spacer size={1} />
						<div style={{ display: "flex", gap: "10px" }}>
							<Button size="small" onClick={addReviewCycle}>
								{reviewCycleData.editingReviewCycleId ? "Update Review Cycle" : "Add Review Cycle"}
							</Button>
							{reviewCycleData.editingReviewCycleId && (
								<Button
									size="small"
									color="gray"
									onClick={() => {
										cancelEditReviewCycle();
										hideError();
									}}
								>
									Cancel
								</Button>
							)}
						</div>
						{error &&
							(error.includes("Cycle Name") ||
								error.includes("Template") ||
								error.includes("subject")) && (
								<>
									<Spacer size={2} />
									<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
										<Text variant="h3" weight="bold" style={{ color: "red" }}>
											{error}
										</Text>
									</div>
									<Spacer size={1} />
								</>
							)}
						<Spacer size={2} />
						{reviewCycleData.reviewCycles.length > 0 && (
							<ReviewCyclesTable
								reviewCycles={reviewCycleData.reviewCycles}
								reviewTemplates={reviewTemplateData.reviewTemplates}
								onEdit={editReviewCycle}
								onDelete={deleteReviewCycle}
							/>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default ReviewData;
